import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  ArrowLeft, Loader2, Check, X, AlertTriangle,
  Mail, Calendar, Tag, Send, Clock, Users,
  FileText, Wand2, Sparkles, ChevronDown,
  AtSign, MessageSquare, Eye, Zap, TestTube,
} from "lucide-react";

const EMAIL_TYPES = [
  { value: "regular", label: "Regular Campaign", icon: Send, desc: "Standard email blast to your list" },
  { value: "ab_test", label: "A/B Test", icon: TestTube, desc: "Test subject lines or content variants" },
  { value: "automated", label: "Automated / Drip", icon: Zap, desc: "Triggered email sequences" },
];

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white text-sm font-medium ${type === "success" ? "bg-green-600" : "bg-red-600"}`}>
      {type === "success" ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
      {message}
      <button onClick={onClose} className="ml-2 hover:opacity-70"><X className="w-3.5 h-3.5" /></button>
    </div>
  );
}

export default function CreateEmail() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [templates, setTemplates] = useState<any[]>([]);
  const [lists, setLists] = useState<any[]>([]);

  // Form
  const [name, setName] = useState("");
  const [type, setType] = useState("regular");
  const [templateId, setTemplateId] = useState("");
  const [listId, setListId] = useState("");
  const [fromName, setFromName] = useState("MigraTeck");
  const [fromEmail, setFromEmail] = useState("hello@migrateck.com");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [preheader, setPreheader] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [tags, setTags] = useState("");

  // A/B Test
  const [subjectB, setSubjectB] = useState("");
  const [splitRatio, setSplitRatio] = useState(50);

  // AI
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    api.get<any>("/email/templates?limit=100").then(d => setTemplates(d.templates || d || [])).catch(() => {});
    api.get<any>("/email/lists?limit=100").then(d => setLists(d.lists || d || [])).catch(() => {});
  }, []);

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    try {
      const data = await api.post<any>("/ai/generate/email", { prompt: aiPrompt });
      if (data.subject) setSubject(data.subject);
      if (data.preheader) setPreheader(data.preheader);
      if (data.name) setName(prev => prev || data.name);
      setToast({ message: "AI subject line generated!", type: "success" });
    } catch {
      setToast({ message: "AI generation failed", type: "error" });
    }
    setGenerating(false);
  };

  const handleSave = async (draft = false) => {
    if (!name.trim()) { setToast({ message: "Campaign name is required", type: "error" }); return; }
    if (!subject.trim()) { setToast({ message: "Subject line is required", type: "error" }); return; }
    setSaving(true);
    try {
      const payload: any = {
        name: name.trim(),
        type,
        subject: subject.trim(),
        fromName: fromName.trim() || undefined,
        fromEmail: fromEmail.trim() || undefined,
        replyTo: replyTo.trim() || undefined,
        preheader: preheader.trim() || undefined,
        templateId: templateId || undefined,
        listId: listId || undefined,
        scheduledAt: scheduledAt || undefined,
        tags: tags || undefined,
        status: draft ? "draft" : undefined,
      };
      await api.post("/email/campaigns", payload);
      setToast({ message: draft ? "Email draft saved!" : "Email campaign created!", type: "success" });
      setTimeout(() => navigate("/email"), 1200);
    } catch (err: any) {
      setToast({ message: err.message || "Failed to create email campaign", type: "error" });
    }
    setSaving(false);
  };

  const selectedList = lists.find((l: any) => l.id === listId);
  const charCount = subject.length;
  const subjectColor = charCount > 60 ? "text-red-500" : charCount > 40 ? "text-amber-500" : "text-gray-400";

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/email")} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create Email Campaign</h1>
            <p className="text-sm text-gray-500 mt-0.5">Design & send a new email to your subscribers</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => handleSave(true)} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
            <FileText className="w-4 h-4 text-gray-400" />
            Save Draft
          </button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="btn-primary text-sm flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Create Campaign
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign Type */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <label className="block text-sm font-semibold text-gray-900">Campaign Type</label>
            <div className="grid grid-cols-3 gap-3">
              {EMAIL_TYPES.map(t => (
                <button key={t.value} onClick={() => setType(t.value)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl text-center transition-all border ${
                    type === t.value ? "ring-2 ring-purple-500 border-purple-200 bg-purple-50/30" : "border-gray-100 hover:border-gray-200"
                  }`}>
                  <t.icon className={`w-6 h-6 ${type === t.value ? "text-purple-600" : "text-gray-400"}`} />
                  <div>
                    <p className={`text-sm font-medium ${type === t.value ? "text-purple-700" : "text-gray-900"}`}>{t.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Name & Subject */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Campaign Name <span className="text-red-500">*</span></label>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g., June Newsletter, Product Launch Announcement"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-900">Subject Line <span className="text-red-500">*</span></label>
                <span className={`text-xs ${subjectColor}`}>{charCount}/60</span>
              </div>
              <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="Your compelling subject line here..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none" />
            </div>

            {type === "ab_test" && (
              <div>
                <label className="text-sm font-semibold text-gray-900 mb-2 block">Subject Line B (A/B Test)</label>
                <input type="text" value={subjectB} onChange={e => setSubjectB(e.target.value)}
                  placeholder="Alternative subject line..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none" />
                <div className="mt-3">
                  <label className="text-xs text-gray-500 mb-1 block">Split Ratio: {splitRatio}% / {100 - splitRatio}%</label>
                  <input type="range" value={splitRatio} onChange={e => setSplitRatio(parseInt(e.target.value))}
                    min="10" max="90" step="5" className="w-full" />
                </div>
              </div>
            )}

            <div>
              <label className="text-sm font-semibold text-gray-900 mb-2 block">Preview Text / Preheader</label>
              <input type="text" value={preheader} onChange={e => setPreheader(e.target.value)}
                placeholder="Text shown after subject in inbox preview..."
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none" />
              <p className="text-xs text-gray-400 mt-1">Appears in inbox after the subject line</p>
            </div>
          </div>

          {/* Sender Info */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">Sender Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From Name</label>
                <input type="text" value={fromName} onChange={e => setFromName(e.target.value)}
                  placeholder="Your Company Name"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">From Email</label>
                <input type="email" value={fromEmail} onChange={e => setFromEmail(e.target.value)}
                  placeholder="hello@example.com"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Reply-To Email (optional)</label>
              <input type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)}
                placeholder="Same as From Email if empty"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm outline-none" />
            </div>
          </div>

          {/* AI Subject Line Generator */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <button onClick={() => setShowAI(!showAI)}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-violet-500 to-purple-500 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-gray-900">AI Subject Line Generator</p>
                  <p className="text-xs text-gray-500">Generate high open-rate subject lines</p>
                </div>
              </div>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showAI ? "rotate-180" : ""}`} />
            </button>
            {showAI && (
              <div className="px-6 pb-6 space-y-3">
                <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
                  placeholder="Describe the email topic: e.g., 'Summer sale, 40% off all plans, urgency-driven'"
                  rows={3} className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm outline-none resize-none" />
                <button onClick={handleAiGenerate} disabled={generating || !aiPrompt.trim()}
                  className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {generating ? "Generating..." : "Generate Subject Lines"}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Users className="w-4 h-4 text-blue-600" /> Recipient List
            </label>
            <select value={listId} onChange={e => setListId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-purple-500 outline-none">
              <option value="">Select a list...</option>
              {lists.map((l: any) => (
                <option key={l.id} value={l.id}>{l.name} ({l.subscriberCount?.toLocaleString() || 0})</option>
              ))}
            </select>
            {selectedList && (
              <div className="bg-blue-50 rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium text-blue-800">{selectedList.name}</p>
                <p className="text-xs text-blue-600">{selectedList.subscriberCount?.toLocaleString() || 0} subscribers</p>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <FileText className="w-4 h-4 text-green-600" /> Email Template
            </label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white outline-none">
              <option value="">No template (blank)</option>
              {templates.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Clock className="w-4 h-4 text-amber-600" /> Schedule
            </label>
            <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
            <p className="text-xs text-gray-400">Leave empty to send immediately</p>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-900">
              <Tag className="w-4 h-4 text-purple-600" /> Tags
            </label>
            <input type="text" value={tags} onChange={e => setTags(e.target.value)}
              placeholder="newsletter, june, promo"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none" />
          </div>

          {/* Preview Card */}
          <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl border border-purple-100 p-5 space-y-3">
            <h4 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
              <Eye className="w-4 h-4" /> Inbox Preview
            </h4>
            <div className="bg-white rounded-lg p-3 border border-gray-100 space-y-1">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-purple-500 flex items-center justify-center">
                  <AtSign className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-xs font-medium text-gray-900">{fromName || "Sender"}</span>
              </div>
              <p className="text-sm font-semibold text-gray-900 truncate">{subject || "Your subject line here..."}</p>
              <p className="text-xs text-gray-400 truncate">{preheader || "Preview text will appear here"}</p>
            </div>
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
